import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {addDuplicatedPage, addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {getNumberOfPages} from '@Components/poster-editor/poster-editor-reducer';
import {useAppSelector} from '@/hooks';
import {onDeletePage, onMoveDownPage, onMoveUpPage, onPosterPause, onPosterPlay} from '@Components/poster-editor/library/poster-editor-library';
import styles from './page-top-bar.module.scss';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {IconPlayPause} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/icon-play-pause/icon-play-pause';

interface PageControlBarProps {
  pageId: string;
}

export function PageControlBar({...props}: PageControlBarProps): ReactElement<any> | null {
  const pagesLength = useAppSelector(getNumberOfPages);
  const pageData = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages?.pagesHashMap[props.pageId];
  });
  const numOfPages = useGetNumberOfPages();
  const isVideo = useAppSelector((state) => {
    return state.posterEditor.isVideo;
  });
  const pageOrder = useAppSelector((state) => {
    const pageIndex = state.posterEditor.posterObject?.pages?.pageOrder.indexOf(props.pageId);
    return pageIndex !== -1 ? pageIndex : undefined;
  });

  if (!pageData || pageOrder === undefined) {
    return null;
  }

  if (!isVideo) {
    return null;
  }

  const onPlayPauseClick = (shouldPlay: boolean): void => {
    if (shouldPlay) {
      const page = window.posterEditor?.whiteboard?.pages.pagesHashMap[props.pageId];
      if (!page?.isPlaying()) {
        void window.posterEditor?.whiteboard?.pages.seekToPage(props.pageId);
      }
      onPosterPlay();
    } else {
      onPosterPause();
    }
  };

  const getPlayOptionOnly = (): ReactElement => {
    return <IconPlayPause hasMarginRight={false} onClick={onPlayPauseClick} type={IconType.GHOST_UNCOLORED} size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} />;
  };

  const getOptions = (): ReactElement | null => {
    if (isVideo && numOfPages === 1) {
      return getPlayOptionOnly();
    }

    if (numOfPages === 1) {
      return null;
    }

    return getPageEditingOptions();
  };

  const getPageEditingOptions = (): ReactElement<any> => {
    return (
      <>
        <IconPlayPause onClick={onPlayPauseClick} hasMarginRight={false} type={IconType.GHOST_UNCOLORED} size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} />
        <Icon icon="icon-new-page" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} onClick={onAddPage} />
        <Icon icon="icon-copy" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} onClick={onDuplicatePage} />
        {pageOrder === 0 ? null : (
          <Icon
            icon="icon-arrow-top"
            isDisabled={pageOrder === 0}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveUpPage(props.pageId);
            }}
          />
        )}
        {pageOrder === pagesLength - 1 ? null : (
          <Icon
            icon="icon-arrow-bottom"
            isDisabled={pageOrder === pagesLength - 1}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveDownPage(props.pageId);
            }}
          />
        )}
        <Icon
          icon="icon-delete"
          className="color-danger-100"
          isDisabled={pagesLength === 1}
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          onClick={(): void => {
            onDeletePage(props.pageId);
          }}
        />
      </>
    );
  };

  const onDuplicatePage = (): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    addDuplicatedPage(currentPage).catch((e) => {
      console.error(e);
    });
  };

  const onAddPage = (): void => {
    if (pageOrder !== undefined) {
      addNewPage(pageOrder + 1).catch((e) => {
        console.error(e);
      });
    }
  };
  return <div className={`flex-v-row spacing-p-l-1 ${styles.container}`}>{getOptions()}</div>;
}
